import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../apiFetch';

const PILOT = '3ece9bc70890c09e304cb11510ef8586';
const conditions = { modality: 'Modalidade', area: 'Área atendida', hours: 'Horário de entrega', minimumOrder: 'Pedido mínimo' };
const labels = { LOADING: 'Carregando', LOADED: 'Carregado', LOAD_FAILED: 'Falha de leitura',
  SAVING: 'Salvando', SAVED: 'Salvo', SAVE_FAILED: 'Salvamento não confirmado', STALE_REVISION: 'Revisão obsoleta',
  ELIGIBLE: 'Elegível', PENDING: 'Declaração pendente', INELIGIBLE: 'Inelegível' };
const reasons = { HUMAN_DECLARATION_REQUIRED: 'Precisa da sua declaração',
  IRREGULAR_WITHOUT_CURRENTNESS_AUTHORITY: 'Não autoriza uso neste piloto', ITEM_IDENTITY_INVALID: 'Identidade inválida',
  ITEM_SOURCE_AMBIGUOUS: 'Mercadoria duplicada neste ponto', CANONICAL_AUTHORING_REVISION_REQUIRED: 'Revisão canônica indisponível' };
const emptyDelivery = () => ({ providerRole: '', ...Object.fromEntries(Object.keys(conditions).map(key =>
  [key, { mode: '', value: '' }])) });

export default function ProtectedPilotSourcesPanel({ clienteId }) {
  const [phase, setPhase] = useState('LOADING');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [aspects, setAspects] = useState({});
  const [delivery, setDelivery] = useState(emptyDelivery);
  const [deliveryChanged, setDeliveryChanged] = useState(false);
  const sequence = useRef(0);
  const abort = useRef(null);
  const saving = useRef(false);
  const url = `/api/store-map/${encodeURIComponent(clienteId)}/protected-sources`;
  const apply = useCallback(body => {
    if (body.ok !== true || body.clienteId !== clienteId || body.authoringIntegrity !== 'CANONICAL_CAS'
      || typeof body.authoringRevision !== 'string' || !/^[1-9][0-9]{0,18}$/.test(body.authoringRevision)
      || BigInt(body.authoringRevision) > 9223372036854775807n || !Array.isArray(body.sources)
      || !body.sources.every(source => source && typeof source.sourceRef === 'string'
        && Array.isArray(source.reasons) && ['ELIGIBLE', 'PENDING', 'INELIGIBLE'].includes(source.declarationState)
        && (source.sourceType !== 'ITEM' || typeof source.value?.name === 'string' && typeof source.elementId === 'string'))) {
      throw new Error('Fontes sem revisão canônica confirmada.');
    }
    setData(body); setAspects({}); setDeliveryChanged(false);
    setDelivery(body.deliveryDeclaration || emptyDelivery());
  }, [clienteId]);
  const load = useCallback(async () => {
    abort.current?.abort();
    const controller = new AbortController(); abort.current = controller;
    const token = ++sequence.current;
    const timer = setTimeout(() => controller.abort(), 15000);
    setPhase('LOADING'); setError('');
    try {
      const response = await apiFetch(url, { signal: controller.signal });
      const body = await response.json();
      if (!response.ok) throw new Error(body.erro || 'Falha ao carregar fontes.');
      if (token !== sequence.current) return;
      if (controller.signal.aborted) throw new Error('Tempo de carregamento excedido.');
      apply(body); setPhase('LOADED');
    } catch (err) {
      if (token !== sequence.current) return;
      setError(controller.signal.aborted ? 'Tempo de carregamento excedido.' : err.message);
      setPhase('LOAD_FAILED');
    } finally { clearTimeout(timer); }
  }, [url, apply]);
  useEffect(() => {
    if (clienteId === PILOT) load();
    return () => { ++sequence.current; abort.current?.abort(); };
  }, [clienteId, load]);
  async function save() {
    if (saving.current || !data || !['LOADED', 'SAVED', 'SAVE_FAILED'].includes(phase)) return;
    saving.current = true;
    const token = sequence.current;
    const controller = new AbortController(); abort.current = controller;
    const timer = setTimeout(() => controller.abort(), 30000);
    setPhase('SAVING'); setError('');
    try {
      const items = data.sources.filter(source => source.sourceType === 'ITEM' && aspects[source.sourceRef])
        .map(source => ({ elementId: source.elementId, name: source.value.name, aspect: aspects[source.sourceRef] }));
      const response = await apiFetch(url, { method: 'POST', signal: controller.signal,
        body: JSON.stringify({ expectedRevision: data.authoringRevision, items,
          ...(deliveryChanged ? { delivery: Object.fromEntries(['providerRole', ...Object.keys(conditions)].map(k =>
            [k, k === 'providerRole' ? delivery[k] : delivery[k].mode === 'EXPLICIT_NONE'
              ? { mode: 'EXPLICIT_NONE', value: null } : delivery[k]])) } : {}) }) });
      const body = await response.json();
      if (token !== sequence.current) return;
      if (controller.signal.aborted) throw new Error('Salvamento não confirmado; recarregue a revisão canônica.');
      if (!response.ok) {
        setPhase(response.status === 409 && body.code === 'STALE_REVISION' ? 'STALE_REVISION' : 'SAVE_FAILED');
        setError(body.code === 'STALE_REVISION'
          ? `A revisão ${data.authoringRevision} ficou obsoleta. Revisão atual: ${body.actualRevision || 'não confirmada'}. Rascunho preservado.`
          : body.erro || 'Salvamento não confirmado; rascunho preservado.'); return;
      }
      apply(body); setPhase('SAVED');
    } catch (err) {
      if (token === sequence.current) {
        setError(controller.signal.aborted ? 'Salvamento não confirmado. Recarregue para conferir a revisão.' : err.message);
        setPhase('SAVE_FAILED');
      }
    } finally { clearTimeout(timer); saving.current = false; }
  }
  if (clienteId !== PILOT) return null;
  const editable = ['LOADED', 'SAVED', 'SAVE_FAILED'].includes(phase);
  const dirty = Object.keys(aspects).length > 0 || deliveryChanged;
  return <section style={{ border: '1px solid var(--border)', padding: 16, marginBottom: 14 }}>
    <h2 style={{ fontSize: 15 }}>Fontes do piloto protegido</h2>
    <p>{labels[phase]} — última revisão confirmada: {data?.authoringRevision || 'nenhuma'}.</p>
    {error && <p role="alert">{error}</p>}
    <button type="button" disabled={phase === 'SAVING'} onClick={() => {
      if (dirty && !window.confirm('Recarregar descarta o rascunho das declarações. Continuar?')) return;
      load();
    }}>Recarregar declarações</button>
    {data && <>
      <p>Declare somente o que conhece da operação. Sortimento habitual não confirma estoque atual.</p>
      <ul>{data.sources.filter(source => source.sourceType !== 'ITEM').map(source => <li key={source.sourceRef}>
        {source.value?.name || source.sourceType}: {labels[source.declarationState]} — revisão {source.sourceRevision || 'ausente'}.
        {source.reasons.includes('SPATIAL_CONTEXT_STALE') ? ' Contexto espacial desatualizado; não bloqueia fontes independentes.'
          : source.reasons.includes('LEGACY_INELIGIBLE') ? ' Serviço legado precisa ser declarado novamente pelo responsável.'
            : source.reasons.includes('HUMAN_DECLARATION_REQUIRED') ? ` Falta declarar: ${source.missingFields.join(', ')}.` : ''}
      </li>)}</ul>
      {data.sources.filter(source => source.sourceType === 'ITEM').map(source => <div key={source.sourceRef}>
        <label>{source.value.name}{' '}
          <select aria-label={`Aspecto de ${source.value.name}`} disabled={!editable || source.authorable === false}
            value={aspects[source.sourceRef] || source.value.aspect || ''}
            onChange={event => setAspects(previous => ({ ...previous, [source.sourceRef]: event.target.value }))}>
            <option value="" disabled>Aspecto não declarado</option>
            <option value="REGULAR">Regular no sortimento</option>
            <option value="IRREGULAR">Irregular</option>
          </select>
        </label>
        <small> {labels[source.declarationState]} — {source.reasons.map(reason => reasons[reason] || reason).join(', ') || 'declaração elegível'}; revisão {source.sourceRevision || 'ausente'}</small>
      </div>)}
      <fieldset disabled={!editable} style={{ marginTop: 16 }}>
        <legend>Entrega — declaração moderna</legend>
        <label>Responsável{' '}<select aria-label="Responsável pela entrega" value={delivery.providerRole}
          onChange={event => { setDelivery(previous => ({ ...previous, providerRole: event.target.value })); setDeliveryChanged(true); }}>
          <option value="" disabled>Não declarado</option>
          <option value="STORE">A loja realiza a entrega</option>
          <option value="THIRD_PARTY">Entrega por terceiros</option>
        </select></label>
        {Object.entries(conditions).map(([key, label]) => <div key={key}>
          <label>{label}{' '}<select aria-label={`${label}: condição`} value={delivery[key]?.mode || ''}
            onChange={event => { const mode = event.target.value;
              setDelivery(previous => ({ ...previous, [key]: { mode, value: mode === 'EXPLICIT_NONE' ? null : '' } })); setDeliveryChanged(true); }}>
            <option value="" disabled>Não declarado</option>
            <option value="VALUE">Informar condição</option>
            <option value="EXPLICIT_NONE">Declaro que não há condição</option>
          </select></label>
          {delivery[key]?.mode === 'VALUE' && <input aria-label={label} maxLength={500}
            value={delivery[key].value || ''} onChange={event => {
              setDelivery(previous => ({ ...previous, [key]: { mode: 'VALUE', value: event.target.value } })); setDeliveryChanged(true);
            }} />}
        </div>)}
        <p>Horários da loja e a opção antiga de delivery não preenchem esta declaração.</p>
      </fieldset>
      <button type="button" disabled={!editable || !dirty} onClick={save}>Salvar declarações na revisão {data.authoringRevision}</button>
      <p>Corredor protegido: {data.pilotEligible ? 'Elegível para o próximo gate' : 'Não habilitado'}. A declaração de fontes não ativa a reprodução.</p>
    </>}
  </section>;
}
